import { useState, useEffect, useRef } from 'react';

interface GoogleLoginButtonProps {
  onSuccess: (token: string) => void;
  onError?: (error: Error) => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (parent: HTMLElement, options: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export function GoogleLoginButton({ onSuccess, onError }: GoogleLoginButtonProps) {
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Verifica se o script já foi carregado
    if (window.google) {
      console.log('✅ Google SDK já estava carregado');
      setIsGoogleLoaded(true);
      initializeGoogleSignIn();
      return;
    }

    // Verifica se o script já está no DOM
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      console.log('⏳ Script do Google já está sendo carregado...');
      existingScript.addEventListener('load', () => {
        console.log('✅ Script do Google carregado (existente)');
        setIsGoogleLoaded(true);
        initializeGoogleSignIn();
      });
      return;
    }

    // Load Google OAuth SDK
    console.log('📥 Carregando script do Google...');
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('✅ Script do Google carregado');
      setIsGoogleLoaded(true);
      initializeGoogleSignIn();
    };
    script.onerror = (error) => {
      console.error('❌ Falha ao carregar Google OAuth SDK:', error);
      setIsGoogleLoaded(false);
    };
    document.body.appendChild(script);

    return () => {
      // Não remove o script para evitar recarregar
    };
  }, []);

  const initializeGoogleSignIn = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

    console.log('🔧 Inicializando Google Sign-In');
    console.log('📍 Origin:', window.location.origin);
    console.log('🔑 Client ID:', clientId);

    if (!clientId) {
      console.warn('Google Client ID not configured. Set VITE_GOOGLE_CLIENT_ID in .env');
      return;
    }

    if (window.google && buttonRef.current) {
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
        });

        // Renderiza o botão nativo do Google
        window.google.accounts.id.renderButton(
          buttonRef.current,
          {
            theme: 'outline',
            size: 'large',
            width: buttonRef.current.offsetWidth || 400,
            text: 'continue_with',
            locale: 'pt-BR',
          }
        );

        console.log('✅ Google Sign-In inicializado e botão renderizado');
      } catch (error) {
        console.error('❌ Erro ao inicializar Google Sign-In:', error);
      }
    }
  };

  const handleCredentialResponse = async (response: any) => {
    console.log('🎉 Callback do Google chamado!');
    console.log('📦 Response completo:', response);
    console.log('🎫 Credential:', response?.credential ? 'Presente' : 'Ausente');

    try {
      if (response.credential) {
        console.log('✅ Credential válido, chamando onSuccess...');
        onSuccess(response.credential);
        console.log('✅ onSuccess completado');
      } else {
        console.error('❌ Nenhuma credencial recebida');
        throw new Error('No credential received from Google');
      }
    } catch (error) {
      console.error('❌ Erro no handleCredentialResponse:', error);
      if (onError) {
        onError(error as Error);
      }
    }
  };

  return (
    <div className="w-full">
      {!isGoogleLoaded && (
        <div 
          className="w-full py-3 px-4 text-center"
          style={{
            border: '1px solid #E6E6F0',
            borderRadius: '6px',
            color: 'var(--wl-text-muted)',
            backgroundColor: '#FFF'
          }}
        >
          Loading Google...
        </div>
      )}
      <div 
        ref={buttonRef} 
        className="w-full flex justify-center items-center min-h-[44px]"
        style={{ display: isGoogleLoaded ? 'flex' : 'none' }}
      />
    </div>
  );
}
